import { PLANS_BY_LANG, tr } from "@/lib/i18n";
import { Check, Crown, X, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import PrivacyPolicy from "@/components/PrivacyPolicy";
import Terms from "@/components/Terms";
import { trackEvent } from "@/lib/analytics";

/**
 * Pricing page — showcases every plan with a full feature list, launch badge,
 * best-value badge (Annual), and a "Your current subscription" summary block
 * at the top. Prices are FINAL — VAT/tax are included in the Stripe prices
 * (no "+ VAT" annotations anywhere).
 */

// Small formatter used by the current-plan section
function fmtDate(iso, lang) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(lang === "pl" ? "pl-PL" : "en-US", {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch { return iso.slice(0, 10); }
}

function daysBetween(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (isNaN(ms)) return null;
  return Math.max(0, Math.ceil(ms / (24 * 3600 * 1000)));
}

// Renders the "Your current subscription" summary at the top of the pricing page
function CurrentSubscription({ lang, access, user, t }) {
  if (!user) return null;
  const plan = access?.plan || "free";
  const exp = access?.plan_expires_at;

  // What to show as the plan's user-facing name
  const nameByPlan = {
    free:    "7 DAY FREE",
    single:  "Single Scene",
    sprint:  "48h Sprint",
    monthly: "LEO Monthly",
    light:   "LEO Light",
    annual:  "LEO Annual",
  };
  const display = nameByPlan[plan] || plan;

  // Line describing when it ends
  let expiryLine = null;
  const dateStr = fmtDate(exp, lang);
  const daysLeft = daysBetween(exp);

  if (plan === "free") {
    expiryLine = dateStr && (
      <>
        <span className="mono text-[11px] uppercase tracking-widest text-stone-500">{t("pr.current_expires")}:</span>
        <span className="ml-2 text-stone-200">{dateStr}</span>
        {daysLeft != null && (
          <span className="ml-3 mono text-[11px] uppercase tracking-widest text-stone-500">
            · {daysLeft} {t("pr.current_days_word")} {t("pr.current_days_left")}
          </span>
        )}
      </>
    );
  } else if (plan === "monthly" || plan === "light") {
    expiryLine = dateStr && (
      <>
        <span className="mono text-[11px] uppercase tracking-widest text-stone-500">{t("pr.current_next_renewal")}:</span>
        <span className="ml-2 text-stone-200">{dateStr}</span>
      </>
    );
  } else if (plan === "annual") {
    expiryLine = dateStr && (
      <>
        <span className="mono text-[11px] uppercase tracking-widest text-stone-500">{t("pr.current_valid_until")}:</span>
        <span className="ml-2 text-stone-200">{dateStr}</span>
      </>
    );
  } else if (plan === "sprint" || plan === "single") {
    expiryLine = dateStr && (
      <>
        <span className="mono text-[11px] uppercase tracking-widest text-stone-500">{t("pr.current_expires")}:</span>
        <span className="ml-2 text-stone-200">{dateStr}</span>
      </>
    );
  }

  return (
    <div
      data-testid="current-subscription-block"
      className="paper p-6 mb-8 border-amber-500/25 bg-gradient-to-br from-amber-500/5 via-stone-900 to-stone-900"
    >
      <div className="mono text-[11px] tracking-[0.3em] text-amber-500 uppercase" data-testid="current-subscription-title">
        {t("pr.current_section_title")}
      </div>
      <div className="mt-3 flex flex-wrap items-baseline gap-2">
        <div className="serif text-3xl text-stone-100" data-testid="current-plan-name">
          {t("pr.current_plan_label")}: <span className="text-amber-400">{display}</span>
        </div>
      </div>
      {expiryLine && <div className="mt-2 text-sm" data-testid="current-plan-expiry">{expiryLine}</div>}
    </div>
  );
}

// Render order (identical for PL/EN/JA, desktop/mobile).
const PLAN_ORDER = ["leo_start", "leo_monthly", "leo_light", "leo_sprint_48h", "leo_annual", "leo_single_scene", "free_trial"];
// Which feature indices (into each plan's existing `features` array) are shown before expand.
// Any features not listed here are still rendered — just after the expand toggle.
const VISIBLE_INDICES = {
  leo_start:        [0, 2, 5, 6, 9],
  leo_monthly:      [0, 2, 5, 6, 8],
  leo_light:        [0, 1, 2, 3, 4],
  leo_sprint_48h:   [0, 2, 4, 1, 9],
  leo_annual:       [0, 11, 3, 5, 8],
  leo_single_scene: [0, 1, 2, 4, 8],
  free_trial:       [0, 2, 4, 7, 5],
};

// Backend classification mirror: which plan ids are subscriptions (recurring)
// vs one-time. Kept in sync with PLAN_META in /app/backend/server.py — do NOT
// derive from marketing labels.
const SUBSCRIPTION_PLAN_IDS = new Set(["leo_start", "leo_monthly", "leo_light", "leo_annual"]);

export default function Pricing() {
  const [busy, setBusy] = useState(null);
  const [expandedPlan, setExpandedPlan] = useState(null);
  // Per-plan checkbox state: { [plan_id]: { terms: bool, immediate: bool } }
  // Both default false, both required before we call POST /payments/checkout.
  const [consents, setConsents] = useState({});
  const { user, access, lang } = useAuth();
  const t = (k) => tr(k, lang);
  const plans = PLANS_BY_LANG[lang] || PLANS_BY_LANG.pl;
  const sortedPlans = PLAN_ORDER
    .map((id) => plans.find((p) => p.id === id))
    .filter(Boolean);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Optional ?src=scene|career|paths — the callers who land users on the
  // general pricing page pass this so we can attribute funnel steps.
  const source = searchParams.get("src");
  const selectedRef = useRef(null);
  const startedRef = useRef(false);
  const viewedRef = useRef(false);

  const isEN = lang === "en";
  const consentOf = (id) => consents[id] || { terms: false, immediate: false };
  const setConsent = (id, key, val) =>
    setConsents((prev) => ({ ...prev, [id]: { ...consentOf(id), [key]: val } }));

  useEffect(() => {
    // StrictMode double-invokes effects in dev — guard against duplicate emits.
    if (viewedRef.current) return;
    viewedRef.current = true;
    trackEvent("paywall_viewed", { kind: "pricing", source: source || null });
    return () => {
      if (startedRef.current) {
        trackEvent("checkout_abandoned", {
          kind: "pricing", source: source || null, plan: selectedRef.current,
        });
      }
    };
    // eslint-disable-next-line
  }, []);

  const buy = async (lookup_key) => {
    if (!user) { navigate("/login"); return; }
    // HARD-BLOCK on missing checkbox acceptance. Backend re-validates.
    const c = consentOf(lookup_key);
    if (!c.terms || !c.immediate) return;
    let effective_key = lookup_key;
    if (lookup_key && !lookup_key.endsWith("_pln") && !lookup_key.endsWith("_usd")
        && lookup_key !== "free_trial") {
      effective_key = `${lookup_key}_${lang === "pl" ? "pln" : "usd"}`;
    }
    const plan = plans.find((p) => p.id === lookup_key) || {};
    const currency = lang === "pl" ? "PLN" : "USD";
    trackEvent("paywall_plan_selected", {
      kind: "pricing", source: source || null, plan: lookup_key,
      price: plan.price, currency,
    });
    selectedRef.current = lookup_key;
    setBusy(lookup_key);
    try {
      trackEvent("checkout_started", {
        kind: "pricing", source: source || null, plan: lookup_key,
        price: plan.price, currency, lookup_key: effective_key,
      });
      startedRef.current = true;
      const r = await api.post("/payments/checkout", {
        lookup_key: effective_key,
        origin_url: window.location.origin,
        terms_accepted: true,
        privacy_acknowledged: true,
        immediate_service_requested: true,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("pr.fail"));
    } finally { setBusy(null); }
  };

  return (
    <div>
      <div className="mono text-[11px] tracking-[0.4em] text-amber-500 uppercase">{t("pr.eyebrow")}</div>
      <h1 className="serif text-5xl text-stone-100 mt-2 max-w-2xl">{t("pr.title")}</h1>
      <p className="text-stone-400 mt-2 max-w-2xl">{t("pr.subtitle")}</p>

      <div className="mt-8">
        <CurrentSubscription lang={lang} access={access} user={user} t={t} />
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mt-4">
        {sortedPlans.map(p => (
          <div
            key={p.id}
            data-testid={`plan-card-${p.id}`}
            className={`paper p-6 flex flex-col relative ${p.highlight ? "border-amber-500/50 shadow-[0_0_0_1px_rgba(245,158,11,0.35)]" : ""}`}
          >
            {/* Badges (top row) */}
            <div className="flex flex-wrap items-center gap-2 min-h-[18px]">
              {p.best_value && (
                <div className="flex items-center gap-1.5 mono text-[10px] tracking-[0.3em] text-amber-500 uppercase" data-testid={`plan-best-value-${p.id}`}>
                  <Crown size={12}/> {t("pr.best_value")}
                </div>
              )}
              {p.launch_badge && (
                <div className="flex items-start gap-1.5 mono text-[10px] leading-[1.4] tracking-[0.25em] text-amber-400/90 uppercase px-2 py-1 rounded border border-amber-500/30 bg-amber-500/5 whitespace-pre-line" data-testid={`plan-launch-${p.id}`}>
                  <Sparkles size={11} className="mt-0.5 shrink-0"/> <span>{typeof p.launch_badge === "string" ? p.launch_badge : t("pr.launch_badge")}</span>
                </div>
              )}
            </div>

            <div className="serif text-2xl text-stone-100 mt-2">{p.name}</div>
            <div className="mono text-[11px] text-stone-500 uppercase tracking-widest">{p.tag}</div>
            {p.description_first && p.description && (
              <p className="mt-3 text-sm text-stone-300 leading-relaxed" data-testid={`plan-description-${p.id}`}>{p.description}</p>
            )}

            <div className="mt-4">
              {p.regular_price && (
                <div className="mono text-sm text-stone-500 line-through" data-testid={`plan-regular-price-${p.id}`}>
                  {p.regular_price}{p.priceSuffix ? ` ${p.priceSuffix}` : ""}
                </div>
              )}
              <div className="flex items-baseline gap-1.5">
                <span className="serif text-4xl text-amber-400" data-testid={`plan-price-${p.id}`}>{p.price}</span>
                {p.priceSuffix && (
                  <span className="mono text-xs text-stone-400" data-testid={`plan-price-suffix-${p.id}`}>{p.priceSuffix}</span>
                )}
              </div>
              {p.save_line && (
                <div className="mono text-[11px] text-amber-400/90 mt-1 tracking-wider" data-testid={`plan-save-${p.id}`}>
                  {p.save_line}
                </div>
              )}
            </div>

            {/* Features */}
            <div className="mt-5 mono text-[10px] uppercase tracking-widest text-stone-500">{t("pr.includes")}</div>
            {(() => {
              const isOpen = expandedPlan === p.id;
              const allIdx = p.features.map((_, i) => i);
              const visIdx = VISIBLE_INDICES[p.id] || allIdx;
              const restIdx = allIdx.filter((i) => !visIdx.includes(i));
              const orderedFeatures = [...visIdx, ...restIdx].map((i) => p.features[i]);
              const visibleCount = visIdx.length;
              const shown = isOpen ? orderedFeatures : orderedFeatures.slice(0, visibleCount);
              const hasMore = restIdx.length > 0;
              return (
                <>
                  <ul className="mt-2 space-y-2 flex-1">
                    {shown.map((f, i) => (
                      <li key={i} className="flex gap-2 text-sm text-stone-300 leading-relaxed">
                        <Check size={14} className="text-amber-500 mt-0.5 shrink-0"/>{f}
                      </li>
                    ))}
                  </ul>
                  {hasMore && (
                    <button
                      type="button"
                      data-testid={`plan-toggle-${p.id}`}
                      onClick={() => setExpandedPlan(isOpen ? null : p.id)}
                      className="mono text-[10px] uppercase tracking-widest text-amber-500 mt-3 self-start hover:text-amber-400"
                    >
                      {isOpen ? t("pr.collapse_details") : t("pr.expand_details")}
                    </button>
                  )}
                </>
              );
            })()}

            {/* Exclusions */}
            {p.excludes && p.excludes.length > 0 && (
              <div className="mt-4 pt-3 border-t border-stone-800/60">
                <div className="mono text-[10px] uppercase tracking-widest text-stone-500">{t("pr.does_not_include")}</div>
                <ul className="mt-2 space-y-1.5">
                  {p.excludes.map((e, i) => (
                    <li key={i} className="flex gap-2 text-xs text-stone-500 leading-relaxed">
                      <X size={12} className="text-stone-600 mt-0.5 shrink-0"/>{e}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Footer tagline — italicised closing line per plan */}
            {p.footer_line && (
              <div
                data-testid={`plan-footer-${p.id}`}
                className="mt-4 pt-3 border-t border-stone-800/60 text-[13px] italic text-stone-400 leading-relaxed"
              >
                {p.footer_line}
              </div>
            )}

            {/* CTA — hidden for 7 DAY FREE (automatic plan) */}
            {!p.free_trial && (() => {
              const c = consentOf(p.id);
              const canBuy = c.terms && c.immediate;
              const isSub = SUBSCRIPTION_PLAN_IDS.has(p.id);
              const showFirstThen = !!p.regular_price && isSub;
              return (
                <div className="mt-6 space-y-3">
                  {showFirstThen && (
                    <div className="text-xs text-stone-400 leading-relaxed" data-testid={`plan-first-then-${p.id}`}>
                      {isEN
                        ? `First period: ${p.price}${p.priceSuffix ? " " + p.priceSuffix : ""}. Then: ${p.regular_price}${p.priceSuffix ? " " + p.priceSuffix : ""}.`
                        : `Pierwszy okres: ${p.price}${p.priceSuffix ? " " + p.priceSuffix : ""}. Następnie: ${p.regular_price}${p.priceSuffix ? " " + p.priceSuffix : ""}.`}
                    </div>
                  )}
                  {p.bottom_price_line && (
                    <div className="text-xs text-stone-300 leading-relaxed" data-testid={`plan-bottom-price-${p.id}`}>
                      {p.bottom_price_line}
                    </div>
                  )}
                  {isSub && (
                    <div className="text-xs text-stone-400 leading-relaxed" data-testid={`plan-renew-notice-${p.id}`}>
                      {isEN
                        ? "Your subscription renews automatically. You can cancel future renewals at any time."
                        : "Subskrypcja odnawia się automatycznie. Możesz anulować kolejne odnowienie w dowolnym momencie."}
                    </div>
                  )}
                  <label className="flex items-start gap-2 text-xs text-stone-300 leading-relaxed cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid={`plan-consent-terms-${p.id}`}
                      checked={c.terms}
                      onChange={(e) => setConsent(p.id, "terms", e.target.checked)}
                      className="mt-0.5 shrink-0"
                    />
                    <span>
                      {isEN ? (
                        <>I accept the{" "}
                          <a
                            href="/pricing#terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-amber-500 underline"
                            data-testid={`plan-consent-terms-link-${p.id}`}
                          >LEO Terms of Service</a>
                          {" "}and confirm that I have read the{" "}
                          <a
                            href="/pricing#privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-amber-500 underline"
                            data-testid={`plan-consent-privacy-link-${p.id}`}
                          >Privacy Policy</a>.
                        </>
                      ) : (
                        <>Akceptuję{" "}
                          <a
                            href="/pricing#terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-amber-500 underline"
                            data-testid={`plan-consent-terms-link-${p.id}`}
                          >Regulamin LEO</a>
                          {" "}i potwierdzam zapoznanie się z{" "}
                          <a
                            href="/pricing#privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-amber-500 underline"
                            data-testid={`plan-consent-privacy-link-${p.id}`}
                          >Polityką Prywatności</a>.
                        </>
                      )}
                    </span>
                  </label>
                  <label className="flex items-start gap-2 text-xs text-stone-300 leading-relaxed cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid={`plan-consent-immediate-${p.id}`}
                      checked={c.immediate}
                      onChange={(e) => setConsent(p.id, "immediate", e.target.checked)}
                      className="mt-0.5 shrink-0"
                    />
                    <span>
                      {isEN
                        ? "I request that the LEO service begins immediately after purchase, before the expiry of the 14-day withdrawal period. I acknowledge that if I exercise my right of withdrawal, I may be required to pay for the service provided up to the time of withdrawal, in accordance with applicable law."
                        : "Żądam rozpoczęcia świadczenia usługi LEO od razu po zakupie, przed upływem 14-dniowego terminu na odstąpienie od umowy. Przyjmuję do wiadomości, że w przypadku odstąpienia mogę być zobowiązany/a do zapłaty za świadczenie spełnione do chwili odstąpienia, zgodnie z obowiązującymi przepisami."}
                    </span>
                  </label>
                  <button
                    data-testid={`plan-buy-${p.id}`}
                    onClick={() => buy(p.id)}
                    disabled={busy === p.id || !canBuy}
                    className={`${p.highlight ? "pill-btn justify-center" : "ghost-btn justify-center"} w-full disabled:opacity-40 disabled:cursor-not-allowed`}
                  >
                    {busy === p.id ? t("pr.opening") : (isEN ? "Buy and pay" : "Kupuję i płacę")}
                  </button>
                </div>
              );
            })()}
            {p.free_trial && (
              <div className="mt-6 mono text-[10px] uppercase tracking-widest text-stone-500 text-center py-2 border border-dashed border-stone-800 rounded" data-testid={`plan-auto-${p.id}`}>
                {t("pr.current_active")} · Auto
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 mb-2">
        <div className="mono text-[11px] tracking-[0.3em] text-amber-500 uppercase mb-3">
          {lang === "pl" ? "AKTORZY O LEO" : "WHAT ACTORS SAY ABOUT LEO"}
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="paper p-6 border-amber-500/25">
            <p className="text-stone-100 text-base leading-relaxed italic">
              {lang === "pl"
                ? "„Wrzuciłam sobie scenę i... Mega jest, bo cały proces robi z Tobą — i to uziemienie na koniec. Świetne, naprawdę. Kawał dobrej roboty.”"
                : "“I uploaded a scene. What I love is that LEO takes you through the entire process — including the grounding exercise at the end. It’s really great. Such a fantastic job.”"}
            </p>
            <div className="mono text-[11px] text-amber-500/90 tracking-widest uppercase mt-3">
              {lang === "pl" ? "— Marlena, aktorka" : "— Marlena, actress"}
            </div>
          </div>
          <div className="paper p-6 border-amber-500/25">
            <p className="text-stone-100 text-base leading-relaxed italic">
              {lang === "pl"
                ? "„Wrzuciłem do LEO scenę na casting. LEO wszystko rozłożył na czynniki pierwsze i dużo więcej mi uświadomił.”"
                : "“I uploaded a scene for an audition to LEO. LEO broke everything down for me and made me realize so much more.”"}
            </p>
            <div className="mono text-[11px] text-amber-500/90 tracking-widest uppercase mt-3">
              {lang === "pl" ? "— Krzysztof, aktor" : "— Krzysztof, actor"}
            </div>
          </div>
        </div>
      </div>

      <p className="mono text-[11px] text-stone-500 mt-8 tracking-wider">{t("pr.card_hint")}</p>

      <PrivacyPolicy lang={lang} />
      <Terms lang={lang} />
    </div>
  );
}
